import { type MoodCheckin, type ThoughtRecord, type VoiceLog, WeeklyReport } from '@adhd/shared';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { DayDoses } from '../components/DayDoses.js';
import { DaySummary } from '../components/DaySummary.js';
import { covering } from '../components/DayLifeEvent.js';
import { api, type OwnLifeEvent } from '../lib/api.js';
import { DayFeed } from '../components/DayFeed.js';
import { dayHeading } from '../components/DayHeading.js';
import { ThreadChips } from '../components/ThreadChips.js';

/**
 * Everything you've logged, newest first, grouped by day.
 *
 * This screen exists because the app was previously write-only: you could
 * record a thought record and then had no way to ever see it again. For an app
 * whose stated purpose is working against time blindness, that was the wrong
 * way round — capture is only half of it, and re-reading is where the value
 * actually lands.
 *
 * Unfinished thought records are surfaced at the top rather than buried in the
 * timeline. A record with the distressing thought written out and no balanced
 * response is the one worth coming back to.
 */

/**
 * `day` is the server's, frozen when the thing was written; `at` is the instant.
 *
 * Grouping used to call dayOf(at), which resolves in the BROWSER's current
 * zone — so a week logged in Hawaii regrouped itself the moment the phone flew
 * home, and the same records sat under different dates depending on where you
 * were standing when you looked. The stored day is the one that was true when
 * you lived it, and it is the only one that holds still.
 */
type Entry =
  | { kind: 'record'; at: string; day: string; data: ThoughtRecord }
  | { kind: 'checkin'; at: string; day: string; data: MoodCheckin }
  | { kind: 'journal'; at: string; day: string; data: VoiceLog };

export function History(): JSX.Element {
  const [entries, setEntries] = useState<Entry[] | null>(null);
  // Days the server says had something on them, doses included.
  const [activeDays, setActiveDays] = useState<string[]>([]);
  /* Help now moments per day. Counts only — see /insights/sos-days. */
  const [sosDays, setSosDays] = useState<Map<string, number>>(new Map());
  const [error, setError] = useState<string | null>(null);
  /* One request for the whole screen, not one per day: the list is short and
     every fold needs the same answer. */
  const [lifeEvents, setLifeEvents] = useState<OwnLifeEvent[]>([]);

  useEffect(() => {
    void api.lifeEvents().then(setLifeEvents).catch(() => {});
  }, []);

  const todayKey = new Date().toLocaleDateString('en-CA');
  /*
   * The hash, watched — not read once when the rows land.
   *
   * This depended on `entries` alone, so it fired exactly once: on the load
   * that filled the list. A link from somewhere else worked; a link from one
   * day of the Log to another did not, because clicking it changes the URL and
   * nothing else — same component, same rows, no re-render the effect can see.
   * The address bar said #day-2026-07-08 and the page never moved.
   *
   * `bump` covers the remaining case: tapping the SAME link twice. React Router
   * does not re-navigate to the location you are already at, so the hash does
   * not change and the effect has nothing to react to.
   */
  const { hash } = useLocation();
  const [bump, setBump] = useState(0);
  useEffect(() => {
    const again = (): void => setBump((n) => n + 1);
    window.addEventListener('hashchange', again);
    return () => window.removeEventListener('hashchange', again);
  }, []);

  const load = useCallback(() => {
    return Promise.all([
      api.listThoughtRecords('?limit=100'),
      api.listCheckins(100),
      api.listJournal(100),
      api.activeDays(),
      api.sosDays(),
      // A fourth request, and it buys the medication line on each day heading.
      // The counts live in daily_stats, and /medications/adherence is the route
      // that already reads meds_taken and meds_scheduled out of it a day at a
      // time — a new endpoint to fold them into one of the calls above would be
      // a schema change and a server round trip saved, in that order.
      //

    ])
      .then(([records, checkins, journal, active, sos]) => {
        setActiveDays(active);
        setSosDays(new Map(sos.map((r) => [r.day, r.n])));
        const merged: Entry[] = [
          ...records.items.map((r) => ({ kind: 'record' as const, at: r.occurredAt, day: r.day, data: r })),
          ...checkins.items.map((c) => ({ kind: 'checkin' as const, at: c.recordedAt, day: c.day, data: c })),
          ...journal.items.map((j) => ({ kind: 'journal' as const, at: j.recordedAt, day: j.day, data: j })),
        ].sort((a, b) => b.at.localeCompare(a.at));
        setEntries(merged);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Arriving at one day, from a link somewhere else.
   *
   * Native hash navigation cannot do this: the Log renders a spinner until four
   * requests resolve, so the browser looks for #day-… before the element it
   * names exists, finds nothing, and leaves the reader at the top of a year of
   * days with no clue which one they were sent to. So the jump waits for the
   * rows. `entries` in the dependency list is what makes it wait.
   *
   * `auto`, not `smooth`: this is a destination, not a gesture, and animating a
   * scroll past eleven months of somebody's record to get there is unkind.
   */
  useEffect(() => {
    if (!entries) return;
    const target = hash.match(/^#day-(\d{4}-\d{2}-\d{2})$/)?.[1];
    if (!target) return;
    scrollToDay(target);
  }, [entries, hash, bump]);

  if (error) return <div className="banner banner-error">{error}</div>;

  if (!entries) {
  
  return (
      <div style={{ display: 'grid', placeItems: 'center', minHeight: '50dvh' }}>
        <div className="spinner" aria-label="Loading" />
      </div>
    );
  }

  const unfinished = entries.filter(
    (e): e is Extract<Entry, { kind: 'record' }> => e.kind === 'record' && !e.data.isComplete,
  );

  // A record drafted from a journal entry is shown beneath that entry rather
  // than as its own row. It is the same event — listing it twice, unconnected,
  // read as two things that happened to be near each other in time. It can also
  // land on the far side of midnight from the entry it came from, which put the
  // pair on different days entirely.
  //
  // It still appears in "Worth finishing" above if it is unfinished; that
  // section is about getting back to it, and burying it would defeat the point.
  const recordsById = new Map(
    entries.flatMap((e) => (e.kind === 'record' ? [[e.data.id, e.data] as const] : [])),
  );
  const derivedRecords = new Map<string, ThoughtRecord>();
  for (const entry of entries) {
    if (entry.kind !== 'journal' || !entry.data.linkedThoughtRecordId) continue;
    const record = recordsById.get(entry.data.linkedThoughtRecordId);
    // Absent when the record is older than this page of results. Leave the
    // entry unpaired rather than rendering a link to something not loaded.
    if (record) derivedRecords.set(entry.data.id, record);
  }
  const nested = new Set([...derivedRecords.values()].map((r) => r.id));
  /*
   * The mood he gave an entry, keyed by that entry.
   *
   * Drawn on the entry's own line rather than as a card of its own — the same
   * shape Today uses, so a linked mood does not read one way here and another
   * way there. The check-in itself still appears in the day's fold, unchanged:
   * that fold's label derives its count AND its average from the very array it
   * lists, so removing a card from it would silently move the day's printed
   * average away from the server's, from the week strip and from Trends.
   */
  const moodFor = new Map<string, MoodCheckin>();
  for (const e of entries) {
    if (e.kind === 'checkin' && e.data.voiceLogId) moodFor.set(e.data.voiceLogId, e.data);
  }

  const byDay = new Map<string, Entry[]>();
  for (const entry of entries) {
    if (entry.kind === 'record' && nested.has(entry.data.id)) continue;
    byDay.set(entry.day, [...(byDay.get(entry.day) ?? []), entry]);
  }

  /*
   * Days with nothing in them, said out loud.
   *
   * This list was built purely from records that exist, so a day you logged
   * nothing on simply was not here — and an absent day is indistinguishable
   * from a lost one. Five consecutive days went missing from the record and
   * there was no way to tell, from this screen, whether that was a quiet week
   * or a bug.
   *
   * Collapsed rather than listed. During a bad stretch the honest answer is
   * several weeks of nothing, and making somebody scroll past every one of
   * those days to reach the days they did manage something is its own small
   * cruelty. One line, expandable.
   */
  /*
   * A day with a dose on it is not a day with nothing on it.
   *
   * These gaps were built from the three things this screen lists, so ticking
   * off a dose and doing nothing else put the day inside "1 day with nothing
   * logged" — while the streak on Trends counted the very same day. The week
   * strip had this bug and was fixed by counting doses; the Log kept it.
   *
   * The server owns the definition (`/insights/active-days`, the same
   * five-term sum as the streak) rather than this screen inferring it, so the
   * two cannot drift apart again.
   */
  const rows = withGaps(
    [...new Set([...byDay.keys(), ...activeDays])].sort().reverse(),
  );

  /*
   * Where a running event should SAY something, as opposed to merely mark a day.
   *
   * Twice per run and no more: on the newest covered day in the list, which is
   * the end he meets first coming down from today, and on the day it began. The
   * days between carry the stroke and no words. Sixty covered days currently
   * print the name sixty times and "Going on since July 7" sixty times, which is
   * a quarter of the page, one sentence.
   *
   * If the day it began is not in the list — nothing was logged that day, so no
   * section exists for it — the foot lands on the oldest covered day that IS in
   * the list, and only there does the event print a date of its own.
   */
  const marks = new Map<string, DayMark>();
  {
    const dayRows = rows.flatMap((r) => (r.kind === 'day' ? [r.day] : []));
    for (const d of dayRows) {
      const rail = covering(lifeEvents, d);
      if (rail.length > 0) marks.set(d, { rail, heads: [], feet: [], footDay: new Map() });
    }
    for (const e of lifeEvents) {
      const covered = dayRows.filter((d) => covering([e], d).length > 0);
      if (covered.length === 0) continue;
      const newest = covered[0]!;
      const oldest = covered[covered.length - 1]!;
      // Every event gets its own head and foot. Two overlapping runs therefore
      // produce two named ends, which may land on the same day.
      marks.get(newest)?.heads.push(e);
      // Only when the two ends are different days; a one-day run says it once.
      if (oldest !== newest) {
        marks.get(oldest)?.feet.push({ e, dated: oldest !== e.startedOn });
      }
      // Where its foot is, so a mid-run line can link to it.
      for (const d of covered) {
        const m = marks.get(d);
        if (m) m.footDay.set(e.id, oldest);
      }
    }
  }

  return (
    <div className="stack-lg stack">
      <header className="page-header">
        <Link className="linkish" to="/days" style={{ display: 'inline-block', marginBottom: 'var(--space-2)' }}>
          ‹ Days
        </Link>
        <h1>The list</h1>
        <p>
          {entries.length === 0
            ? 'Nothing here yet.'
            : `${entries.length} ${entries.length === 1 ? 'entry' : 'entries'}, newest first. All of it stays on this server.`}
        </p>
        {/* Just the list (14 Oct 2026). The button, the four chips, the weeks
            and the month that used to sit here were the old home's furniture;
            Today has the door and the thought, the rail has the rest, and Days
            has the weeks and the month. */}
      </header>

      {entries.length === 0 && (
        <div className="card empty stack">
          <p>Once you log a check-in or work through a thought, it'll show up here.</p>
          <div className="stack-sm stack" style={{ marginTop: 'var(--space-4)' }}>
            <Link className="btn btn-primary btn-block" to="/check-in">
              Quick check-in
            </Link>
            <Link className="btn btn-secondary btn-block" to="/thought-record">
              Work through a thought
            </Link>
          </div>
        </div>
      )}



      {unfinished.length > 0 && (
        <section className="stack">
          <h2>Worth finishing</h2>
          <p className="muted">
            These have the thought written down but not the fairer version yet — that last step is
            where it starts to shift.
          </p>
          <div className="stack-sm stack">
            {unfinished.map((e) => (
              <Link key={e.data.id} className="option" to={`/thought-record/${e.data.id}`}>
                <strong>{truncate(e.data.automaticThought, 70)}</strong>
                <span>{formatDate(e.at)} · unfinished</span>
              </Link>
            ))}
          </div>
        </section>
      )}

      {rows.map((row, i) => {
        const rung = monthRung(rows, i);
        return (
          <Fragment key={row.kind === 'gap' ? `gap-${row.days[0]}` : row.day}>
            {rung && <p className="month-rung">{rung}</p>}
            {row.kind === 'gap' ? (
              <GapRow days={row.days} />
            ) : (
              <DaySection
                day={row.day}
                dayEntries={byDay.get(row.day) ?? []}
                derivedRecords={derivedRecords}
                    today={todayKey}
                onChanged={load}
                sosCount={sosDays.get(row.day) ?? 0}
                mark={marks.get(row.day)}
              />
            )}
          </Fragment>
        );
      })}

    </div>
  );
}




function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max).trimEnd()}…` : text;
}

/**
 * `YYYY-MM-DD` in the browser's own timezone.
 *
 * Not `toISOString().slice(0, 10)`, which is the UTC day: far enough east, an
 * entry written in the evening files itself under tomorrow, and far enough
 * west a morning one lands on yesterday.
 *
 * It also has to agree with the server, which is new. The medication counts on
 * each heading come out of daily_stats, whose `day` is the user's own calendar
 * day — so grouping on the UTC day would hang one day's doses under a
 * neighbouring date for anyone not on UTC. A miscounted day on a psychiatric
 * record is not a cosmetic error; it is a false entry, and it is one the person
 * reading it back has no way to catch.
 */
function localDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}


/**
 * The one line under a week's dates in the Log.
 *
 * It replaces "· 3 days logged", which was a count standing directly above the
 * days it counted — the app agreeing or disagreeing with itself in the same
 * scroll, for no gain. This says what KIND of week it was to open, which is the
 * only thing a row has to answer: is there something to read here.
 */
export function summarise(r: WeeklyReport, withFigures = true): string {
  if (r.summaryMd !== null) {
    /* A fold passes false. Open, its body prints the full figures line, and a
       summary carrying the first two of them put "36 check-ins · 10 entries"
       two lines above "36 check-ins · 10 entries · 73 thought records · 38 Help
       now · -2 average" — the app saying the same thing twice in one glance.
       On the Log the row is alone and has to sell itself, so it keeps them. */
    if (!withFigures) return '';
    const figures = [
      r.stats.checkins > 0 && `${r.stats.checkins} check-in${r.stats.checkins === 1 ? '' : 's'}`,
      r.stats.voiceLogs > 0 && `${r.stats.voiceLogs} entr${r.stats.voiceLogs === 1 ? 'y' : 'ies'}`,
    ].filter(Boolean) as string[];
    return figures.length > 0 ? figures.join(' · ') : 'Written up';
  }
  if (r.status === 'no_data') return 'Nothing logged that week';
  if (r.status === 'pending') return 'Still being written';
  if (r.status === 'failed') return 'The write-up did not finish — open to try again';
  return 'Nothing written that week to summarise';
}

/* Exported so WeekPage prints the same week heading as the row that links to
   it. Two copies of a date format is how three date formats happened. */
export function formatWeek(start: string, end: string): string {
  const f = (d: string): string =>
    new Date(`${d}T12:00:00`).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  return `${f(start)} – ${f(end)}`;
}

export function formatDayShort(day: string): string {
  return new Date(`${day}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short' });
}

/** One day in the Log. Extracted so the list can interleave gap rows. */
function DaySection({
  day,
  dayEntries,
  derivedRecords,
  today,
  onChanged,
  sosCount,
  mark,
}: {
  day: string;
  dayEntries: Entry[];
  derivedRecords: Map<string, ThoughtRecord>;
  today: string;
  onChanged: () => void;
  sosCount: number;
  mark?: DayMark;
}): JSX.Element {
  return (
        /* The id is the destination of every "N entries" and "N thought
           records" link in a day fold elsewhere in the app. */
        <section
          className={`stack day-section day-section-ruled${mark ? ' day-section-railed' : ''}`}
          id={`day-${day}`}
        >
          {/*
            The date and what the day holds are ONE thing, not two.
            
            The tally was a sibling in this `.stack`, so it took the same 1rem
            gap as everything else and floated in a band of its own — closer to
            the fold below it than to the date it describes. A subtitle set at
            body distance from its heading reads as an unrelated line.
            
            Non-zero terms only: a row of zeroes says how much he opened the app
            rather than anything about the day. Check-ins are deliberately not
            here — they are counted on the fold label below, which is the fold
            that hides them, so each count sits with the thing it counts.
          */}
          <div className="day-head">
            <h2>{formatDay(day)}</h2>
            {/* Between the date and the counts, so the event's name has the date
                above it and its own date below it, and never sits directly on a
                figure line. Outside the fold's <summary> as well: a <summary>
                has one accessible name, so anything inside it concatenates the
                event with the day's numbers for a screen reader, for copy and
                for print. */}
            <RunEnds mark={mark} day={day} />
            {tallyOf(dayEntries, sosCount) && (
              <p className="faint">{tallyOf(dayEntries, sosCount)}</p>
            )}
          </div>
          {/* The day, added up. Closed by default and only fetched once opened:
              the Log can hold a year of days, and asking the server to total up
              every one of them because someone scrolled past would be rude to
              the server and slow for the reader. */}
          {/* One fold per day, and its summary line carries the mood aggregate.
              That placement is the whole fix: the total now labels the very fold
              that hides the check-ins, so the aggregate and the individual
              check-ins are structurally incapable of being on screen together.
              Two folds at two indentations, each summarising the same day, read
              as rival accounts of it.

              Computed here from the check-ins already fetched for the list, so a
              closed day still costs no request and the lazy fetch inside keeps
              its reason for existing. */}
          {/*
            The day's reading, above the day.
            
            It went below the entries for one round, to stop the newest thing
            being buried when the fold was open. That was solving the wrong
            problem: the burial came from the day being split into two groups,
            and once the contents became one list there was nothing left to bury.
            
            A headline above the thing it describes is the ordinary arrangement,
            and it is what he asked for.
          */}
          <details className="day-summary-fold">
            <summary>{summaryLine(dayEntries)}</summary>
            <DaySummaryFold day={day} />
          </details>
          {/*
            One list, in time order, exactly as Today draws it.
            
            The day used to be grouped by kind: entries and records out here,
            check-ins folded away inside. He looked at a check-in at 9:16 PM
            sitting above one at 11:37 AM with a journal entry below both and
            said it felt weird they were not together — and he is right. The
            grouping was invisible; it read as one list that was out of order.
            
            So the Log uses DayFeed, which means a check-in, an entry and a
            record are ordered against each other by when they happened, a mood
            saved from an entry hangs off it, and neither screen can drift from
            the other because there is one component.
          */}
          <DayFeed
            items={dayEntries}
            records={derivedRecords}
            today={today}
            onCheckinRemoved={onChanged}
          />
        </section>
  );
}

/**
 * A run of days with nothing recorded.
 *
 * Collapsed, and expandable. Absence has to be visible — five consecutive days
 * vanished from this screen and there was no way to tell a quiet week from a
 * bug — but a bad month is a lot of empty days, and making somebody scroll past
 * every one to reach the days they did manage something is its own small
 * cruelty.
 *
 * Opening it lists them, because a day with nothing logged is still a day you
 * may want to open: it is where you would go to say something bigger started
 * then.
 */
function GapRow({ days }: { days: string[] }): JSX.Element {
  const [open, setOpen] = useState(false);
  if (open) {
    return (
      <>
        {days.map((d) => (
          <section className="stack-sm stack" key={d}>
            <h2 className="history-gap-heading">{formatDay(d)}</h2>
            <p className="faint" style={{ margin: 0 }}>
              Nothing logged.
            </p>
          </section>
        ))}
      </>
    );
  }
  return (
    <button className="history-gap" onClick={() => setOpen(true)}>
      {days.length} day{days.length === 1 ? '' : 's'} with nothing logged
    </button>
  );
}

/**
 * The day list with runs of empty days turned into one collapsed marker.
 *
 * Bounded to the span that actually has records: it never invents days before
 * the first thing ever logged, and never runs forward past the last, so a new
 * account does not open on a wall of nothing.
 */
type Row = { kind: 'day'; day: string } | { kind: 'gap'; days: string[] };

function withGaps(daysNewestFirst: string[]): Row[] {
  if (daysNewestFirst.length === 0) return [];
  const have = new Set(daysNewestFirst);
  const rows: Row[] = [];
  let gap: string[] = [];

  const flush = (): void => {
    if (gap.length > 0) {
      rows.push({ kind: 'gap', days: gap });
      gap = [];
    }
  };

  const newest = daysNewestFirst[0]!;
  const oldest = daysNewestFirst[daysNewestFirst.length - 1]!;
  for (let d = newest; d >= oldest; d = shiftDay(d, -1)) {
    if (have.has(d)) {
      flush();
      rows.push({ kind: 'day', day: d });
    } else {
      gap.push(d);
    }
  }
  flush();
  return rows;
}

/** YYYY-MM-DD arithmetic at local midday, so a DST hop cannot skip a date. */
function shiftDay(day: string, delta: number): string {
  const d = new Date(`${day}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return localDay(d);
}

function formatDay(day: string): string {
  if (day === localDay(new Date())) return 'Today';
  if (day === localDay(new Date(Date.now() - 86_400_000))) return 'Yesterday';
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  // Built from its parts in local time to match the key it was grouped under.
  // Parsed as UTC and formatted locally, as it was, the heading could name a
  // different day from the entries sitting beneath it.
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

/**
 * Defers the fetch until the fold is actually opened.
 *
 * `<details>` renders its children whether or not it is open, so without this
 * every day on screen would total itself up on load — which for a year of
 * history is a great many requests nobody asked for.
 */
/**
 * The day's summary line, built from what the list already holds.
 *
 * No request: a closed day must stay free, or the Log would total up a year of
 * days the moment it rendered. It names the day's mood because that is the fact
 * this fold hides — the check-in rows are inside it — and a fold whose label
 * describes its own contents cannot end up competing with them.
 */
function summaryLine(entries: Entry[]): JSX.Element {
  /*
   * The heading itself is in components/DayHeading.tsx, so that a test can
   * render it. It lived here, in a file that imports react-router and the API
   * client, which meant nothing could render it without standing up most of
   * the app — and it spent a release printing a different verdict on a day
   * than Today's strip printed for the same day. See the comment on
   * `dayHeading`.
   *
   * What stays here is the one thing that needs this screen's own types:
   * pulling the check-in moods out of a mixed entry list. `entries` is sorted
   * descending by `at` (the sort at the top of this file), so this comes out
   * newest-first, which is the order `dayHeading` documents.
   *
   * The life event is NOT in this line. It was joined to the figures by the
   * same "·" that separates them from each other, so the label read "How this
   * day went — 🔖 Toby is dying · Alright · +1.7 average": a middot asserts
   * the things either side of it are peers, which files a death as a
   * comparable entry, and read as a sentence it answers "how did this day go"
   * with the bereavement. DayEventMark draws it, above the counts, in its own
   * element.
   */
  return dayHeading(
    entries
      .filter((e): e is Extract<Entry, { kind: 'checkin' }> => e.kind === 'checkin')
      .map((e) => e.data.mood),
  );
}

function DaySummaryFold({ day }: { day: string }): JSX.Element {
  const [opened, setOpened] = useState(false);
  useEffect(() => {
    const el = document.getElementById(`fold-${day}`);
    const parent = el?.closest('details');
    if (!parent) return;
    const onToggle = (): void => {
      if (parent.open) setOpened(true);
    };
    parent.addEventListener('toggle', onToggle);
    return () => parent.removeEventListener('toggle', onToggle);
  }, [day]);

  return (
    <div id={`fold-${day}`} className="stack-sm stack">
      {/* Order: the reading, then the thing you can act on, then the longest
          part. `shownNearby` suppresses the rows the Log already shows — the
          entries are listed above this fold, and the check-ins are inside it. */}
      <DaySummary day={day} active={opened} shownNearby={{ checkins: true, entries: true }} />
      {/* Editable, including days long past. The carry-over on Today only
          reaches last night and only until 4am; this is where a dose noticed
          days later gets corrected, and where a wrong tap can be undone. */}
      <DayDoses day={day} active={opened} />
      {/* No check-in list in here any more: they are in the day's own list
          above, in time order with everything else. */}
    </div>
  );
}

/**
 * What a day holds, as a line: "2 entries · 1 thought record".
 *
 * Counted from the rows already loaded for the list, so a closed day still
 * costs no request — the same reason the fold below it is lazy.
 */
function tallyOf(entries: Entry[], sos = 0): string {
  const n = (k: Entry['kind']): number => entries.filter((e) => e.kind === k).length;
  const bits = [
    n('checkin') > 0 && `${n('checkin')} check-in${n('checkin') === 1 ? '' : 's'}`,
    /*
     * "Help now", the name on the button and the nav tab — never "SOS", which
     * appears on no screen he has ever pressed. Second, matching the week
     * strip's order, so the two tallies of one day read the same way.
     */
    sos > 0 && `${sos} Help now`,
    n('journal') > 0 && `${n('journal')} entr${n('journal') === 1 ? 'y' : 'ies'}`,
    n('record') > 0 && `${n('record')} thought record${n('record') === 1 ? '' : 's'}`,
  ].filter(Boolean) as string[];
  return bits.join(' · ');
}

type DayMark = {
  /** Every event covering this day. */
  rail: OwnLifeEvent[];
  /** Events whose newest covered day this is — the end he meets first. */
  heads: OwnLifeEvent[];
  /** Events whose oldest covered day this is. `dated` when that is not the day it began. */
  feet: { e: OwnLifeEvent; dated: boolean }[];
  /** Event id -> the day its run starts on, so an unnamed line can link there. */
  footDay: Map<string, string>;
};

/**
 * The two ends of a run, and nothing in between.
 *
 * The head is the newest covered day — the end he meets first coming down from
 * today. The foot is the day it began. Neither prints a date of its own: the
 * day's own heading is two lines above, so "Began this day" points at it. The
 * one exception is a run whose first day was never logged, where the foot lands
 * on the oldest day that exists and has to say which day it means.
 *
 * Never the quote. `describe()` falls back to the person's own sentence when an
 * event has no label, and rendering that bare here would put his words from a
 * hard hour into a list he scrolls past — the fold quotes them properly.
 */
/*
 * Scroll to a day of this list.
 *
 * The landing is sometimes short of the top, and that is the page ending rather
 * than a miss: measured at 586px on a 60-day list, where scrollY 16982 plus a
 * 900px viewport is exactly the 17882px page. A day near the bottom cannot be
 * brought to the top because there is nothing below it to scroll up. Do not
 * "fix" this with settle passes or offsets — a previous attempt added two extra
 * animation frames and moved it by nothing at all.
 */
function scrollToDay(day: string): boolean {
  const el = document.getElementById(`day-${day}`);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'auto', block: 'start' });
  return true;
}



/**
 * The threads running on this day, as chips — the one rendering of a thread
 * on a day (14 Oct 2026). This used to draw names at a run's ends and a
 * "🔖 Ongoing life event" tag in between; the chip says the same with its
 * count, and opens the thread's page, where the controls are.
 */
function RunEnds({ mark, day }: { mark?: DayMark; day: string }): JSX.Element | null {
  if (!mark || mark.rail.length === 0) return null;
  return <ThreadChips events={mark.rail} day={day} />;
}

/** The month, once, where it changes. A calendar, not a tally. */
function monthRung(rows: Row[], i: number): string | null {
  const dayOf = (r: Row | undefined): string =>
    !r ? '' : r.kind === 'day' ? r.day : (r.days[0] ?? '');
  const month = (d: string): string => d.slice(0, 7);
  if (i === 0) return null;
  const here = month(dayOf(rows[i]));
  const above = month(dayOf(rows[i - 1]));
  if (here === above) return null;
  return new Date(`${dayOf(rows[i])}T12:00:00`)
    .toLocaleDateString(undefined, { month: 'long', year: 'numeric' })
    .toUpperCase();
}

/* The local `signedMood` alias is gone with the day heading that used it: the
   last reader of a mean on this screen now lives in components/DayHeading.tsx
   and imports `signedMean` from @adhd/shared directly. */
